/**
 * Icon Renderer Utilities
 *
 * Provides conditional rendering for game icons that can be either:
 * - Image paths (e.g., "/logos/game.png", "https://example.com/icon.png")
 * - Text/emoji icons (e.g., "⚾", "DM", "PL")
 *
 * This supports the transition from text-based icons to image logos
 * while maintaining backward compatibility with existing games.
 */

import React from 'react';

/**
 * Checks if the given iconUrl represents an image path (URL or relative path)
 * vs a text/emoji icon.
 *
 * @param iconUrl - The icon URL or text to check
 * @returns true if iconUrl starts with "/" or "http" (indicating an image path)
 *
 * @example
 * isImageIconUrl('/logos/game.png')  // true
 * isImageIconUrl('https://cdn.example.com/icon.png')  // true
 * isImageIconUrl('http://example.com/icon.png')  // true
 * isImageIconUrl('⚾')  // false
 * isImageIconUrl('DM')  // false
 * isImageIconUrl('')  // false
 */
export function isImageIconUrl(iconUrl: string): boolean {
  if (!iconUrl || typeof iconUrl !== 'string') {
    return false;
  }

  return iconUrl.startsWith('/') || iconUrl.startsWith('http');
}

/**
 * Props for the GameIcon component
 */
interface GameIconProps {
  /** The icon URL (image path) or text/emoji to display */
  iconUrl: string;
  /** Background color for text icons (not used for image icons) */
  backgroundColor?: string;
  /** Size in pixels (applies to both width and height). Ignored if sizeClassName is provided. */
  size?: number;
  /**
   * Tailwind classes for width/height sizing (e.g., "w-[14vw] max-w-[56px] h-[14vw] max-h-[56px]").
   * When provided, overrides the size prop for responsive/dynamic sizing.
   */
  sizeClassName?: string;
  /** Additional CSS classes */
  className?: string;
  /** Alt text for image icons */
  alt?: string;
}

/**
 * GameIcon Component
 *
 * Renders either an image (for URL/path icons) or a colored div with text
 * (for emoji/text icons). Maintains consistent sizing regardless of type.
 *
 * @example
 * // Image icon
 * <GameIcon iconUrl="/logos/perfect-lineup.png" size={64} alt="Perfect Lineup" />
 *
 * // Text/emoji icon
 * <GameIcon iconUrl="⚾" backgroundColor="#003366" size={64} />
 *
 * // With fallback text
 * <GameIcon iconUrl="DM" backgroundColor="#FF6600" size={48} />
 */
export function GameIcon({
  iconUrl,
  backgroundColor = '#666666',
  size = 64,
  sizeClassName,
  className = '',
  alt = 'Game icon'
}: GameIconProps) {
  // When sizeClassName is provided, use Tailwind classes for sizing (responsive/dynamic)
  // Otherwise fall back to fixed pixel size via inline styles
  const useResponsiveSizing = !!sizeClassName;

  const baseStyles: React.CSSProperties = useResponsiveSizing
    ? { borderRadius: 10, flexShrink: 0 }
    : { width: size, height: size, borderRadius: 10, flexShrink: 0 };

  const sizeClasses = useResponsiveSizing ? `${sizeClassName} aspect-square` : '';

  if (isImageIconUrl(iconUrl)) {
    // Render image icon
    return (
      <img
        src={iconUrl}
        alt={alt}
        className={`rounded-[10px] object-cover ${sizeClasses} ${className}`}
        style={baseStyles}
      />
    );
  }

  // Render text/emoji icon
  return (
    <div
      className={`flex items-center justify-center text-white font-bebas-neue rounded-[10px] ${sizeClasses} ${className}`}
      style={{
        ...baseStyles,
        backgroundColor,
        // For responsive sizing, use relative font size; otherwise use proportional
        fontSize: useResponsiveSizing ? '50%' : size * 0.5
      }}
    >
      {iconUrl}
    </div>
  );
}
